import moment from "moment";

export const calculateEndDate = (
  startDate: string, 
  frequency: string, 
  gap: number, 
  occurrences: number, 
  weekNumber?: number | null, 
  dayOfWeek?: number | null, 
  month?: number,
  daysOfWeek?: number[],
): string => {
  if (!startDate || !frequency || !gap || !occurrences) return '';
  
  const startMoment = moment(startDate);
  let endMoment;
  
  switch (frequency) {
    case 'daily':
      endMoment = startMoment.clone().add((occurrences - 1) * gap, 'days');
      break;
      
    case 'weekly':
      endMoment = calculateWeeklyEndDate(startMoment, gap, occurrences, daysOfWeek);
      break;
      
    case 'monthly':
      endMoment = calculateMonthlyEndDate(startMoment, gap, occurrences, weekNumber, dayOfWeek);
      break;
      
    case 'yearly':
      endMoment = calculateYearlyEndDate(startMoment, 1, occurrences, weekNumber, dayOfWeek, month); // yearly events can't have gaps
      break;
      
    default:
      return '';
  }
  
  return endMoment ? endMoment.format('YYYY-MM-DD') : '';
};

const calculateWeeklyEndDate = (
  startMoment: moment.Moment, 
  gap: number, 
  occurrences: number, 
  daysOfWeek?: number[]
): moment.Moment => {
  if (!daysOfWeek || daysOfWeek.length === 0) {
    // Simple weekly - same day each week
    return startMoment.clone().add((occurrences - 1) * gap, 'weeks');
  }

  // Complex weekly - multiple days of week
  const occurrenceDates: moment.Moment[] = [];
  let currentWeek = startMoment.clone();
  const startDay = startMoment.isoWeekday();

  while (occurrenceDates.length < (occurrences*daysOfWeek.length)) {
    // Check each day of the week in this week
    for (const dayOfWeek of [...daysOfWeek].sort()) {
      const targetDate = currentWeek.clone().add(dayOfWeek - startDay, 'days');
      
      // Only include if it's on or after the start date
      if (targetDate.isSameOrAfter(startMoment, 'day')) {
        occurrenceDates.push(targetDate);
        
        // Stop if we've found enough occurrences
        if (occurrenceDates.length >= (occurrences*daysOfWeek.length)) {
          break;
        }
      }
    }

    // Move to next week according to gap
    currentWeek.add(gap, 'weeks');
  }  
  // console.log("occurrenceDates", occurrenceDates.map(date => date.format('YYYY-MM-DD')));
  return occurrenceDates[(occurrences*daysOfWeek.length) - 1];
};

const calculateMonthlyEndDate = (
  startMoment: moment.Moment, 
  gap: number, 
  occurrences: number, 
  weekNumber?: number | null, 
  dayOfWeek?: number | null
): moment.Moment => {
  if (weekNumber === undefined || weekNumber === null || dayOfWeek === undefined || dayOfWeek === null) {
    // Simple monthly - same day each month
    return startMoment.clone().add((occurrences - 1) * gap, 'months');
  }
  
  // Complex monthly - specific week and day of month
  const occurrenceDates: moment.Moment[] = [];
  let currentMonth = startMoment.clone().startOf('month');
  
  while (occurrenceDates.length < occurrences) {
    const targetDate = findDateInMonth(currentMonth, weekNumber, dayOfWeek);
    
    // Only include if it's on or after the start date
    if (targetDate.isSameOrAfter(startMoment, 'day')) {
      occurrenceDates.push(targetDate);
    }
    
    // Move to next month according to gap
    currentMonth.add(gap, 'months');
  }
  
  return occurrenceDates[occurrences - 1];
};

const calculateYearlyEndDate = (
  startMoment: moment.Moment, 
  gap: number, 
  occurrences: number, 
  weekNumber?: number | null, 
  dayOfWeek?: number | null, 
  month?: number
): moment.Moment => {
  if (month === undefined) {
    // Simple yearly - same date each year
    return startMoment.clone().add((occurrences - 1) * gap, 'years');
  }

  if (weekNumber === undefined || weekNumber === null || dayOfWeek === undefined || dayOfWeek === null) {
    // Yearly on specific month and day
    return startMoment.clone().month(month - 1).add((occurrences - 1) * gap, 'years');
  }
  
  // Complex yearly - specific week, day, and month
  const occurrenceDates: moment.Moment[] = [];
  let currentYear = startMoment.clone().startOf('year');
  
  while (occurrenceDates.length < occurrences) {
    const targetMonth = currentYear.clone().month(month - 1).startOf('month');
    const targetDate = findDateInMonth(targetMonth, weekNumber, dayOfWeek);
    
    // Only include if it's on or after the start date
    if (targetDate.isSameOrAfter(startMoment, 'day')) {
      occurrenceDates.push(targetDate);
    }
    
    // Move to next year according to gap
    currentYear.add(gap, 'years');
  }
  
  return occurrenceDates[occurrences - 1];
};

export const findDateInMonth = (
  monthStart: moment.Moment, 
  weekNumber: number, 
  dayOfWeek: number
): moment.Moment => {
  const month = monthStart.clone();
  
  if (weekNumber === -1) {
    // Last occurrence of dayOfWeek in the month
    const lastDayOfMonth = month.clone().endOf('month');
    const lastWeekday = lastDayOfMonth.clone();
    
    // Move backwards to find the last occurrence of the target day
    while (lastWeekday.isoWeekday() !== dayOfWeek) {
      lastWeekday.subtract(1, 'day');
    }
    
    return lastWeekday;
  } else {
    // Nth occurrence of dayOfWeek in the month (1st, 2nd, 3rd, 4th)
    const firstDayOfMonth = month.clone().startOf('month');
    let targetDate = firstDayOfMonth.clone();
    
    // Find the first occurrence of the target day
    while (targetDate.isoWeekday() !== dayOfWeek) {
      targetDate.add(1, 'day');
    }
    
    // Add weeks to get to the Nth occurrence
    targetDate.add((weekNumber - 1) * 7, 'days');
    
    // Check if we're still in the same month
    if (targetDate.month() !== month.month()) {
      throw new Error(`Week ${weekNumber} of day ${dayOfWeek} does not exist in ${month.format('YYYY-MM')}`);
    }
    
    return targetDate;
  }
};

// console.log('Daily example:', calculateEndDate('2024-01-01', 'daily', 2, 5)); // Every 2 days, 5 occurrences
// console.log('Weekly example:', calculateEndDate('2024-01-03', 'weekly', 3, 3, undefined, undefined, undefined, [1,5])); // Every week, 3 occurrences
// console.log('Monthly example (2nd Tuesday):', calculateEndDate('2024-01-01', 'monthly', 1, 3, 2, 2)); // 2nd Tuesday of each month, 3 occurrences
// console.log('Yearly example (last Friday of March):', calculateEndDate('2024-01-01', 'yearly', 1, 2, -1, 5, 3)); // Last Friday of March each year, 2 occurrences

export const calculateOccurrences = (
  startDate: string,
  endDate: string,
  frequency: string,
  gap: number,
  weekNumber?: number,
  dayOfWeek?: number,
  month?: number,
  daysOfWeek?: number[]
): number => {
  if (!startDate || !endDate || !frequency || !gap) return 0;
  
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);
  
  if (endMoment.isBefore(startMoment)) return 0;
  
  switch (frequency) {
    case 'daily':
      return calculateDailyOccurrences(startMoment, endMoment, gap);
      
    case 'weekly':
      return calculateWeeklyOccurrences(startMoment, endMoment, gap, daysOfWeek);
      
    case 'monthly':
      return calculateMonthlyOccurrences(startMoment, endMoment, gap, weekNumber, dayOfWeek);
      
    case 'yearly':
      return calculateYearlyOccurrences(startMoment, endMoment, weekNumber, dayOfWeek, month);
      
    default:
      return 0;
  }
};

const calculateDailyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  gap: number
): number => {
  const daysDifference = endMoment.diff(startMoment, 'days');
  return Math.floor(daysDifference / gap) + 1;
};

const calculateWeeklyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  gap: number,
  daysOfWeek?: number[]
): number => {
  if (!daysOfWeek || daysOfWeek.length === 0) {
    // Simple weekly - same day each week
    const weeksDifference = endMoment.diff(startMoment, 'weeks');
    return Math.floor(weeksDifference / gap) + 1;
  }

  // Complex weekly - multiple days of week
  let count = 0;
  let currentWeek = startMoment.clone();
  const startDay = startMoment.isoWeekday();

  while (currentWeek.isSameOrBefore(endMoment, 'week')) {
    // Check each day of the week in this week
    for (const dayOfWeek of [...daysOfWeek].sort()) {
      const targetDate = currentWeek.clone().add(dayOfWeek - startDay, 'days');
      
      // Check if this occurrence falls within our date range
      if (targetDate.isSameOrAfter(startMoment, 'day') && 
          targetDate.isSameOrBefore(endMoment, 'day')) {
        count++;
      }
    }

    // Move to next week according to gap
    currentWeek.add(gap, 'weeks');
  }

  count = Math.round(count / daysOfWeek.length); // 8 events, 2 per week, the whole thing is repeated 4 times, that's the number we want
  return count;
};

const calculateMonthlyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  gap: number,
  weekNumber?: number,
  dayOfWeek?: number
): number => {
  if (weekNumber === undefined || dayOfWeek === undefined) {
    // Simple monthly - same day each month
    const monthsDifference = endMoment.diff(startMoment, 'months');
    return Math.floor(monthsDifference / gap) + 1;
  }
  
  // Complex monthly - specific week and day of month
  let count = 0;
  let currentMonth = startMoment.clone().startOf('month');
  
  while (currentMonth.isSameOrBefore(endMoment, 'month')) {
    try {
      const targetDate = findDateInMonth(currentMonth, weekNumber, dayOfWeek);
      
      // Check if this occurrence falls within our date range
      if (targetDate.isSameOrAfter(startMoment, 'day') && 
          targetDate.isSameOrBefore(endMoment, 'day')) {
        count++;
      }
    } catch (error) {
      // Skip months where the target week/day doesn't exist
    }
    
    // Move to next month according to gap
    currentMonth.add(gap, 'months');
  }
  
  return count;
};

const calculateYearlyOccurrences = (
  startMoment: moment.Moment,
  endMoment: moment.Moment,
  weekNumber?: number,
  dayOfWeek?: number,
  month?: number
): number => {
  if (month === undefined) {
    // Simple yearly - same date each year
    const yearsDifference = endMoment.diff(startMoment, 'years');
    return yearsDifference + 1;
  }
  
  if (weekNumber === undefined || dayOfWeek === undefined) {
    // Yearly on specific month and day
    const yearsDifference = endMoment.diff(startMoment, 'years');
    return yearsDifference + 1;
  }
  
  // Complex yearly - specific week, day, and month
  let count = 0;
  let currentYear = startMoment.clone().startOf('year');
  
  while (currentYear.isSameOrBefore(endMoment, 'year')) {
    try {
      const targetMonth = currentYear.clone().month(month - 1).startOf('month');
      const targetDate = findDateInMonth(targetMonth, weekNumber, dayOfWeek);
      
      // Check if this occurrence falls within our date range
      if (targetDate.isSameOrAfter(startMoment, 'day') && 
          targetDate.isSameOrBefore(endMoment, 'day')) {
        count++;
      }
    } catch (error) {
      // Skip years where the target week/day doesn't exist in the specified month
    }
    
    // Move to next year (gap is always 1 for yearly)
    currentYear.add(1, 'years');
  }
  
  return count;
};

//console.log('Daily example:', calculateOccurrences('2024-01-01', '2024-01-10', 'daily', 2)); // Every 2 days
//console.log('Weekly example:', calculateOccurrences('2024-01-01', '2024-01-29', 'weekly', 1)); // Every week
//console.log('Monthly example (2nd Tuesday):', calculateOccurrences('2024-01-01', '2024-06-30', 'monthly', 1, 2, 2)); // 2nd Tuesday of each month
//console.log('Yearly example (last Friday of March):', calculateOccurrences('2024-01-01', '2026-12-31', 'yearly', 1, -1, 5, 3)); // Last Friday of March each year